import { CheckCircle2, AlertTriangle, AlertOctagon, HelpCircle } from "lucide-react";

/**
 * HealthBadge — visual traffic-light indicator.
 *
 * level: 'ok' | 'warning' | 'critical' | undefined
 * size: 'xs' | 'sm' | 'md' | 'lg'
 * variant: 'badge' (icon+label) | 'dot' (just dot) | 'icon' (just icon)
 * reasons: optional array of human-readable strings shown in tooltip
 * label: optional override label
 */
export default function HealthBadge({ level = "ok", size = "sm", variant = "badge", reasons = [], label, testid, className = "" }) {
  const cfg = LEVELS[level] || LEVELS.ok;
  const Icon = cfg.icon;
  const iconSize = size === "xs" ? "w-3 h-3" : size === "lg" ? "w-5 h-5" : size === "md" ? "w-4 h-4" : "w-3.5 h-3.5";
  const padX = size === "xs" ? "px-1.5" : size === "lg" ? "px-3" : "px-2";
  const padY = size === "xs" ? "py-0.5" : size === "lg" ? "py-1" : "py-0.5";
  const fontSize = size === "xs" ? "text-[10px]" : size === "lg" ? "text-sm" : "text-xs";
  const text = label || cfg.text;
  const tooltip = reasons.length > 0 ? reasons.join(" · ") : text;

  if (variant === "dot") {
    const dotSize = size === "xs" ? "w-1.5 h-1.5" : size === "lg" ? "w-3 h-3" : "w-2 h-2";
    return (
      <span title={tooltip} data-testid={testid} className={`inline-flex shrink-0 rounded-full ${dotSize} ${cfg.dot} ${className}`} />
    );
  }
  if (variant === "icon") {
    return (
      <span title={tooltip} data-testid={testid} className={`inline-flex ${cfg.text_color} ${className}`}>
        <Icon className={iconSize} />
      </span>
    );
  }
  return (
    <span
      title={tooltip}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 ${padX} ${padY} ${fontSize} font-semibold rounded-md border ${cfg.bg} ${cfg.border} ${cfg.text_color} ${className}`}
    >
      <Icon className={iconSize} />
      <span className="uppercase tracking-wider">{text}</span>
    </span>
  );
}

const LEVELS = {
  ok: {
    icon: CheckCircle2,
    text: "OK",
    bg: "bg-emerald-500/10",
    border: "border-emerald-500/30",
    text_color: "text-emerald-400",
    dot: "bg-emerald-500",
  },
  warning: {
    icon: AlertTriangle,
    text: "Warnung",
    bg: "bg-amber-500/10",
    border: "border-amber-500/30",
    text_color: "text-amber-400",
    dot: "bg-amber-500",
  },
  critical: {
    icon: AlertOctagon,
    text: "Kritisch",
    bg: "bg-red-500/10",
    border: "border-red-500/30",
    text_color: "text-red-400",
    dot: "bg-red-500",
  },
  unknown: {
    icon: HelpCircle,
    text: "—",
    bg: "bg-zinc-500/10",
    border: "border-zinc-500/30",
    text_color: "text-zinc-400",
    dot: "bg-zinc-500",
  },
};

/** Helpers for entities the frontend computes itself (time entries, driving logs) */
export function timeEntryHealth(t) {
  if (!t.work_start || !t.work_end || !t.employee_id) {
    return { level: "critical", reasons: ["Daten unvollständig"] };
  }
  if (t.status === "freigegeben") return { level: "ok", reasons: ["Freigegeben"] };
  if (t.status === "geprueft") return { level: "ok", reasons: ["Geprüft"] };
  // open: yellow if older than 3 days
  const today = new Date(); today.setHours(0,0,0,0);
  const d = new Date(t.date + "T00:00:00");
  const daysOld = (today - d) / (1000 * 60 * 60 * 24);
  if (daysOld > 3) return { level: "warning", reasons: ["Offen seit > 3 Tagen"] };
  return { level: "ok", reasons: ["Offen"] };
}

export function drivingLogHealth(d) {
  if (!d.driving_start || !d.driving_end || !d.employee_id || !d.vehicle_id) {
    return { level: "critical", reasons: ["Daten unvollständig"] };
  }
  if (!d.start_km || !d.end_km || d.end_km <= d.start_km) {
    return { level: "warning", reasons: ["KM-Stand prüfen"] };
  }
  return { level: "ok", reasons: [] };
}

/** Document — the API already returns doc_status ('ok' | 'bald' | 'abgelaufen' | 'expired') */
export function documentHealth(doc) {
  const s = doc.doc_status;
  if (s === "abgelaufen") return { level: "critical", reasons: [`Abgelaufen am ${doc.expiry_date}`] };
  if (s === "bald") return { level: "warning", reasons: [`Läuft ab am ${doc.expiry_date}`] };
  return { level: "ok", reasons: doc.expiry_date ? [`Gültig bis ${doc.expiry_date}`] : ["Kein Ablaufdatum"] };
}
